import {
  ATTACKIQ_ID,
  ATOMIC_PACKAGE_CREATION_MODULE_ID,
  AVA_MODULE_ID,
  BPM_MODULE_ID,
  DEEPSURFACE_MODULE_ID,
  DEFENSE_OPTIMIZATION_MODULE_ID,
  DETECTION_RULE_MANAGER_MODULE_ID,
  ENTERPRISE_MODULE_ID,
  FLAT_RATE_FLEX_MODULES,
  FLEX_DETECTION_MODULE_ID,
  FLEX_ENTERPRISE_MODULE_ID,
  FLEX_MODULE_ID,
  FLEX_MONTHLY_MODULE_ID,
  FLEX_PACKAGE_RUNS_MODULE_ID,
  FLEX_YEARLY_MODULE_ID,
  INSIGHTS_MODULE_ID,
  READY_CUSTOM_TESTS_MODULE_ID,
  READY_MODULE_ID,
  REVIEWER_SYSTEM_ROLE_ID,
  SPECIALIST_SYSTEM_ROLE_ID,
  SUPPORT_ADMIN_ROLE_ID,
  AEV_MODULE_ID,
  WATCHTOWER_MODULE_ID,
  WATCHTOWER_DEMO_MODULE_ID,
  SIEM_MODULE_ID,
  EMM_MODULE_ID,
  ACCESS_MANAGER_ROLE_ID,
  READ_ONLY_ADMIN_SYSTEM_ROLE_ID,
  JUPYTER_MODULE_ID
} from '@attackiq/constants';

import { CurrentUser } from './useCurrentUser';

export interface CompanyModuleSelection {
  isFlex: boolean;
  isReady: boolean;
  isFlatRatePlan: boolean;
  isYearlyPlan: boolean;
  isMonthlyPlan: boolean;
  isAnalysisTokenModality: boolean;
  isCreditModality: boolean;
  isEnterprise: boolean;
  isFlexEnterprise: boolean;
  hasFlexDetection: boolean;
  hasCustomTestsEnabled: boolean;
  hasCustomPackagesEnabled: boolean;
  hasAVAEnabled: boolean;
  hasInsightsEnabled: boolean;
  hasAEVEnabled: boolean;
  hasWatchtowerEnabled: boolean;
  isDeepSurface: boolean;
  hasDeepSurfaceEnabled: boolean;
  hasSIEMEnabled: boolean;
  hasEMMEnabled: boolean;
  hasDefenseOptimizationEnabled: boolean;
  hasDetectionRuleManagerEnabled: boolean;
  hasJupyterEnabled: boolean;
  hasBPMEnabled: boolean;
}

export interface CurrentUserCapabilities extends CompanyModuleSelection {
  isGuest: boolean;
  isReviewer: boolean;
  isSpecialist: boolean;
  isMissionControl: boolean;
  isBasic: boolean;
  isReadOnlyAdmin: boolean;
  isDeveloper: boolean;
  isPowerUser: boolean;
  isManager: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isUserManager: boolean;
  isCommunity: boolean;
  isCustomerAdmin: boolean;
  isMSSP: boolean;
  isSingleSignOn: boolean;
  isAirgapped: boolean;
  isTrial: boolean;
  isAccountManager: boolean;
  hasSubdomainsEnabled: boolean;
  isOnPremises: boolean;
  isAIQ: boolean;
  isPartner: boolean;
  salesforceEnabled: boolean;
  partnerHasGlobalSSO: boolean;
  isSupportAdmin: boolean;
  isUsingCustomLogo: boolean;
  hasHiddenFeaturedPackages: boolean;
  mfaSetupRequired: boolean;
  isDefenseOptimizationUser: boolean;
  isRedTeamUser: boolean;
  isExposuresUser: boolean;
  isDetectionManagerUser: boolean;
}

export const getModuleSelection = (
  modules: string[],
  company?: { partner_packages_enabled?: boolean }
): CompanyModuleSelection => {
  return {
    isFlex: modules.includes(FLEX_MODULE_ID),
    isReady: modules.includes(READY_MODULE_ID),
    isFlexEnterprise: modules.includes(FLEX_ENTERPRISE_MODULE_ID),
    isFlatRatePlan: FLAT_RATE_FLEX_MODULES.some(m => modules.includes(m)),
    isYearlyPlan: modules.includes(FLEX_YEARLY_MODULE_ID),
    isMonthlyPlan: modules.includes(FLEX_MONTHLY_MODULE_ID),
    isAnalysisTokenModality: modules.includes(FLEX_PACKAGE_RUNS_MODULE_ID),
    isCreditModality:
      !modules.includes(FLEX_PACKAGE_RUNS_MODULE_ID) && !FLAT_RATE_FLEX_MODULES.some(m => modules.includes(m)),
    isEnterprise: modules.includes(ENTERPRISE_MODULE_ID),
    hasFlexDetection: modules.includes(FLEX_DETECTION_MODULE_ID),
    hasCustomTestsEnabled: modules.includes(READY_CUSTOM_TESTS_MODULE_ID),
    hasCustomPackagesEnabled:
      (company?.partner_packages_enabled ?? false) && modules.includes(ATOMIC_PACKAGE_CREATION_MODULE_ID),
    hasAVAEnabled: modules.includes(AVA_MODULE_ID),
    hasInsightsEnabled: modules.includes(INSIGHTS_MODULE_ID),
    hasWatchtowerEnabled: modules.includes(WATCHTOWER_MODULE_ID) || modules.includes(WATCHTOWER_DEMO_MODULE_ID),
    isDeepSurface: modules.includes(DEEPSURFACE_MODULE_ID),
    hasDeepSurfaceEnabled:
      modules.includes(DEEPSURFACE_MODULE_ID) || modules.includes(EMM_MODULE_ID) || modules.includes(AEV_MODULE_ID),
    hasSIEMEnabled: modules.includes(SIEM_MODULE_ID),
    hasAEVEnabled: modules.includes(AEV_MODULE_ID),
    hasEMMEnabled: modules.includes(EMM_MODULE_ID),
    hasDefenseOptimizationEnabled: modules.includes(DEFENSE_OPTIMIZATION_MODULE_ID),
    hasDetectionRuleManagerEnabled: modules.includes(DETECTION_RULE_MANAGER_MODULE_ID),
    hasJupyterEnabled: modules.includes(JUPYTER_MODULE_ID),
    hasBPMEnabled: modules.includes(BPM_MODULE_ID)
  };
};

const defaultCapabilities: CurrentUserCapabilities = {
  isGuest: false,
  isMissionControl: false,
  isBasic: false,
  isReadOnlyAdmin: false,
  isDeveloper: false,
  isPowerUser: false,
  isManager: false,
  isAdmin: false,
  isSuperAdmin: false,
  isCommunity: false,
  isMSSP: false,
  isSingleSignOn: false,
  isAirgapped: false,
  isTrial: false,
  isAccountManager: false,
  hasSubdomainsEnabled: false,
  isOnPremises: false,
  isAIQ: false,
  isPartner: false,
  salesforceEnabled: false,
  isCustomerAdmin: false,
  isReviewer: false,
  isSpecialist: false,
  partnerHasGlobalSSO: false,
  isSupportAdmin: false,
  isUserManager: false,
  isUsingCustomLogo: false,
  hasHiddenFeaturedPackages: false,
  mfaSetupRequired: false,
  isDefenseOptimizationUser: false,
  isRedTeamUser: false,
  isExposuresUser: false,
  isDetectionManagerUser: false,
  ...getModuleSelection([])
};

export const getCurrentUserCapabilities = (user?: CurrentUser): CurrentUserCapabilities => {
  if (!user) return defaultCapabilities;
  const { id, system_role, company, trial, subdomains_enabled, disable_all_telemetry, is_partner, salesforce_enabled } =
    user;
  const { id: systemRoleId, value: systemRoleValue } = system_role;

  return {
    isReviewer: systemRoleId === REVIEWER_SYSTEM_ROLE_ID,
    isSpecialist: systemRoleId === SPECIALIST_SYSTEM_ROLE_ID,
    isMissionControl: systemRoleId === REVIEWER_SYSTEM_ROLE_ID || systemRoleId === SPECIALIST_SYSTEM_ROLE_ID,
    isSupportAdmin: systemRoleId === SUPPORT_ADMIN_ROLE_ID,
    isGuest: systemRoleValue >= 10,
    isReadOnlyAdmin: systemRoleId === READ_ONLY_ADMIN_SYSTEM_ROLE_ID,
    isBasic: systemRoleValue >= 20,
    isDeveloper: systemRoleValue >= 30,
    isPowerUser: systemRoleValue >= 40,
    isManager: systemRoleValue >= 50,
    isCustomerAdmin: systemRoleValue >= 60,
    isAdmin: systemRoleValue >= 70,
    isSuperAdmin: systemRoleValue >= 80,
    isUserManager: systemRoleId === ACCESS_MANAGER_ROLE_ID,
    isCommunity: company.user_type !== 'Customer',
    isMSSP: company.id === ATTACKIQ_ID,
    isSingleSignOn: company.sso_enabled === 1,
    isAirgapped: disable_all_telemetry === 1,
    isTrial: trial,
    isAccountManager: company.account_manager === id,
    hasSubdomainsEnabled: subdomains_enabled,
    isOnPremises: company.on_premises,
    isAIQ: company.is_attackiq,
    isPartner: is_partner,
    salesforceEnabled: salesforce_enabled,
    partnerHasGlobalSSO: company.partner_has_global_sso_enabled,
    isUsingCustomLogo: company.use_custom_logo ?? false,
    hasHiddenFeaturedPackages: !!company.hide_featured_flex,
    mfaSetupRequired: company.mfa_enabled && !user.is_mfa_enabled,
    isDefenseOptimizationUser: (user.capabilities ?? []).includes('defense_optimization'),
    isRedTeamUser: (user.capabilities ?? []).includes('red_team'),
    isExposuresUser: (user.capabilities ?? []).includes('exposures'),
    isDetectionManagerUser:
      !!user.features?.['detection-manager-override'] ||
      ((user.capabilities ?? []).includes('detection_manager') &&
        company.modules.map(m => m.module).includes(DETECTION_RULE_MANAGER_MODULE_ID)),
    ...getModuleSelection(
      company.modules.map(m => m.module),
      company
    ),
    isReady: company.is_aiq_ready,
    isFlex: company.is_aiq_ready ? !company.hide_flex : company.is_aiq_flex
  };
};
